import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

import { EMPTY_CELL, formatCell, isNumericType } from '../utils/explorerFormat'
import type { ExplorerColumn } from '../types/explorer.types'

interface ExplorerCellProps {
	value: unknown
	column: ExplorerColumn
	className?: string
}

/**
 * One grid cell, rendered by its catalog type (MYD-360).
 *
 * The client holds no per-column rendering rules. Everything comes from the
 * column definition, which is what lets a new backend column appear in the grid
 * correctly formatted without any frontend change.
 */
export function ExplorerCell({ value, column, className }: ExplorerCellProps) {
	const text = formatCell(value, column.type)
	const isEmpty = text === EMPTY_CELL

	if (column.render === 'badge' && !isEmpty) {
		return (
			<span className={className}>
				<Badge variant="secondary">{text}</Badge>
			</span>
		)
	}

	return (
		<span
			className={cn(
				// Numbers right-align so digits line up down the column, and use
				// tabular figures so they do not jitter between rows.
				isNumericType(column.type) && 'block text-right tabular-nums',
				// An empty cell is muted so a sparse column reads as sparse at a
				// glance rather than as a wall of dashes competing with real values.
				isEmpty && 'text-gray-300',
				className,
			)}
			// Dates and long text are truncated in the cell, so the full value
			// stays reachable on hover.
			title={isEmpty ? undefined : text}
		>
			{text}
		</span>
	)
}

export default ExplorerCell
